import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { markAllInboxSeen } from "../../utils/adminInboxStore";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppSelector } from "../../store/hooks";
import { normalizeActivityList } from "../../utils/dashboardActivity";
import { adminStatusBadgeClass, adminStatusLabel } from "./adminStatusUi";
import { getAdminTopBarHeading } from "./adminTopBarHeading";

function typeShortLabel(requesterType) {
  if (requesterType === "individual") return "Individual";
  if (requesterType === "company") return "Company";
  return null;
}

export default function AdminTopBar() {
  const { pathname } = useLocation();
  const heading = getAdminTopBarHeading(pathname);
  const apiOn = isAdminApiEnabled();
  const dashboard = useAppSelector((s) => s.dashboard);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const unread = apiOn ? (dashboard.data?.unread?.total ?? 0) : 0;
  const items = useMemo(() => {
    if (!apiOn) return [];
    return normalizeActivityList(dashboard.data?.activity || []).slice(0, 12);
  }, [apiOn, dashboard.data?.activity]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markAll = useCallback(() => {
    if (!apiOn) markAllInboxSeen();
    setOpen(false);
  }, [apiOn]);

  return (
    <header className="admin-dash__topbar">
      <div className="admin-dash__topbar-lead">
        <span className="admin-dash__topbar-kicker">{heading.kicker}</span>
        <p className="admin-dash__topbar-title">{heading.title}</p>
      </div>
      <div className="admin-dash__topbar-spacer" aria-hidden />
      <div className="admin-dash__notif" ref={wrapRef}>
        <button
          type="button"
          className="admin-dash__notif-btn"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="admin-dash__notif-icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 22a2 2 0 002-2H10a2 2 0 002 2zm6-6V11a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="admin-dash__notif-btn-label">
            <span className="admin-dash__notif-btn-title">Inbox</span>
            <span className="admin-dash__notif-btn-hint">
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </span>
          </span>
          {unread > 0 ? (
            <span className="admin-dash__notif-count" aria-hidden="true">
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
          <span className="admin-dash__sr-only">
            Inbox{unread > 0 ? `, ${unread} unread` : ""}
          </span>
        </button>
        {open ? (
          <div className="admin-dash__notif-panel" role="menu">
            <div className="admin-dash__notif-head">
              <div>
                <span className="admin-dash__notif-title">Recent inbox</span>
                <p className="admin-dash__notif-sub">Requests and contact messages</p>
              </div>
              {unread > 0 && !apiOn ? (
                <button type="button" className="admin-dash__notif-markall" onClick={markAll}>
                  Mark all read
                </button>
              ) : null}
            </div>
            <ul className="admin-dash__notif-list">
              {items.length === 0 ? (
                <li className="admin-dash__notif-empty">Nothing here yet.</li>
              ) : (
                items.map((it) => (
                  <li key={`${it.kind}-${it.id}`}>
                    <Link
                      role="menuitem"
                      className={`admin-dash__notif-item${it.unread ? " admin-dash__notif-item--unread" : ""}`}
                      to={
                        it.kind === "request"
                          ? `/admin/dashboard/requests/${it.id}`
                          : `/admin/dashboard/contact/${it.id}`
                      }
                      onClick={() => setOpen(false)}
                    >
                      <span className="admin-dash__notif-type">{it.typeLabel}</span>
                      <span className="admin-dash__notif-item-title">{it.title}</span>
                      <span className="admin-dash__notif-item-sub">{it.subtitle}</span>
                      <span className="admin-dash__notif-meta">
                        <span className={adminStatusBadgeClass(it.status)}>
                          {adminStatusLabel(it.status)}
                        </span>
                        {typeShortLabel(it.requesterType) ? (
                          <span className="admin-dash__badge-type admin-dash__badge-type--sm">
                            {typeShortLabel(it.requesterType)}
                          </span>
                        ) : null}
                        {it.replyCount > 0 ? (
                          <span className="admin-dash__badge-replies admin-dash__badge-replies--sm">
                            {it.replyCount} repl{it.replyCount === 1 ? "y" : "ies"}
                          </span>
                        ) : null}
                        <time className="admin-dash__notif-time" dateTime={it.createdAt}>
                          {new Date(it.createdAt).toLocaleString()}
                        </time>
                      </span>
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </header>
  );
}
